import type { ScriptElement, ShapeContent, ShapeKind } from '@/lib/schema.types';

const VIEWBOX = 100;

/**
 * SVG path / polygon definitions for all "vector" shapes. They're designed
 * to fill a 0..100 viewBox so that scaling via the wrapper just works.
 */
const SHAPE_GEOMETRY: Partial<Record<ShapeKind, { kind: 'polygon' | 'path'; data: string }>> = {
  triangle: { kind: 'polygon', data: '50,4 96,96 4,96' },
  diamond: { kind: 'polygon', data: '50,4 96,50 50,96 4,50' },
  pentagon: { kind: 'polygon', data: '50,4 96,38 78,94 22,94 4,38' },
  hexagon: { kind: 'polygon', data: '25,6 75,6 96,50 75,94 25,94 4,50' },
  star: {
    kind: 'polygon',
    data: '50,4 61,38 96,38 68,58 79,92 50,72 21,92 32,58 4,38 39,38',
  },
  arrow: {
    // Right-pointing block arrow that fills the box.
    kind: 'polygon',
    data: '4,34 60,34 60,12 96,50 60,88 60,66 4,66',
  },
  heart: {
    kind: 'path',
    data:
      'M50 88 C 14 60, 4 38, 18 22 C 30 8, 46 14, 50 28 C 54 14, 70 8, 82 22 C 96 38, 86 60, 50 88 Z',
  },
  cross: {
    kind: 'polygon',
    data: '38,4 62,4 62,38 96,38 96,62 62,62 62,96 38,96 38,62 4,62 4,38 38,38',
  },
};

export function ShapeElement({ element, fillBox }: { element: ScriptElement; fillBox?: boolean }) {
  const c = (element.content as ShapeContent | undefined) ?? { shape: 'rect' };
  const fill = c.fill ?? (element.style?.backgroundColor as string | undefined) ?? '#6366f1';
  const stroke = c.stroke;
  const radius = (element.style?.borderRadius as number | undefined) ?? 8;

  // CSS-rendered shapes (kept for crisp pixel rendering).
  if (c.shape === 'circle') {
    return (
      <div
        style={{ background: fill, border: stroke ? `2px solid ${stroke}` : undefined }}
        className="aspect-square h-full w-full max-h-20 max-w-[5rem] self-start rounded-full"
      />
    );
  }
  if (c.shape === 'ellipse') {
    return (
      <div
        style={{ background: fill, border: stroke ? `2px solid ${stroke}` : undefined }}
        className="h-16 w-full rounded-full"
      />
    );
  }
  if (c.shape === 'line') {
    return (
      <div style={{ background: fill }} className="h-1 w-full self-center rounded-full" />
    );
  }
  if (c.shape === 'rect') {
    return (
      <div
        style={{
          background: fill,
          borderRadius: radius,
          border: stroke ? `2px solid ${stroke}` : undefined,
          // Freeform mode: fill the authored box — the h-16 class is only a
          // flow-layout default and silently squashed every authored rect.
          ...(fillBox ? { height: '100%' } : {}),
        }}
        className={fillBox ? 'w-full' : 'h-16 w-full'}
      />
    );
  }

  const geometry = SHAPE_GEOMETRY[c.shape];
  if (!geometry) {
    return (
      <div
        style={{ background: fill, borderRadius: radius, ...(fillBox ? { height: '100%' } : {}) }}
        className={fillBox ? 'w-full' : 'h-16 w-full'}
      />
    );
  }

  return (
    <svg
      viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
      preserveAspectRatio="none"
      className="block h-full max-h-32 w-full"
      aria-label={`${c.shape} shape`}
    >
      {geometry.kind === 'polygon' ? (
        <polygon
          points={geometry.data}
          fill={fill}
          stroke={stroke}
          strokeWidth={stroke ? 2 : 0}
          strokeLinejoin="round"
        />
      ) : (
        <path
          d={geometry.data}
          fill={fill}
          stroke={stroke}
          strokeWidth={stroke ? 2 : 0}
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}
